package com.essntl.features.area.ui.addeditarea

import com.essntl.core.utils.utils.loadingmanager.LoadingManager
import com.essntl.core.utils.viewmodel.State
import com.essntl.core.utils.viewmodel.ViewModel
import com.essntl.core.utils.viewmodel.getData
import com.essntl.features.area.domain.model.AreaModel
import com.essntl.features.area.domain.repository.AreaRepository
import com.essntl.features.area.domain.usecase.AddEditAreaUseCase
import com.essntl.features.country.domain.model.CountryModel
import com.essntl.features.country.domain.repository.CountryRepository
import com.essntl.features.proposal.domain.repository.ProposalRepository
import com.essntl.features.supplier.domain.repository.SupplierRepository
import com.essntl.toastmanager.ToastManager
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.update

class AddEditAreaViewModel(
    private val areaRepository: AreaRepository,
    private val addEditAreaUseCase: AddEditAreaUseCase,
    private val countryRepository: CountryRepository,
    private val proposalRepository: ProposalRepository,
    private val supplierRepository: SupplierRepository,
    private val toastManager: ToastManager,
    private val state: MutableStateFlow<AddEditAreaState>,
    override val scope: CoroutineScope,
    override val loadingManager: LoadingManager,
) : ViewModel {

    fun onEvent(event: AddEditAreaEvent) {
        when (event) {
            is AddEditAreaEvent.GetAllCountries -> getAllCountries()
            is AddEditAreaEvent.GetArea -> getArea(event)
            is AddEditAreaEvent.UpdateAreaFields -> updateAreaFields(event)
            is AddEditAreaEvent.AddArea -> addArea()
            is AddEditAreaEvent.EditArea -> editArea()
            is AddEditAreaEvent.DeleteArea -> deleteArea()
            is AddEditAreaEvent.ClearState -> clearState()
            is AddEditAreaEvent.AddCountry -> addCountry(event)
        }
    }

    private fun getAllCountries() =
        getData(
            getData = { countryRepository.getAll() },
            getState = { state.value.getAllCountries },
            setState = { state.value = state.value.copy(getAllCountries = it) },
        )

    private var getAreaJob: Job? = null
    private fun getArea(event: AddEditAreaEvent.GetArea) {
        getAreaJob?.cancel()
        getAreaJob = getData(
            getData = { areaRepository.get(event.id) },
            getState = { state.value.getArea },
            setState = {
                state.value = state.value.copy(
                    getArea = it,
                    isEditEnabled = false,
                )
            },
            onSuccess = { a ->
                state.value.area.value = a
                getCountProposals()
                getCountSuppliers()
            },
        )
    }

    private fun getCountProposals() =
        getData(
            getData = { proposalRepository.getCountProposalsByArea(state.value.area.value.id) },
            getState = { state.value.getProposalsAreaCount },
            setState = { state.value = state.value.copy(getProposalsAreaCount = it) },
        )

    private fun getCountSuppliers() =
        getData(
            getData = { supplierRepository.getCountSuppliersByArea(state.value.area.value.id) },
            getState = { state.value.getSuppliersAreaCount },
            setState = { state.value = state.value.copy(getSuppliersAreaCount = it) },
        )

    private fun updateAreaFields(event: AddEditAreaEvent.UpdateAreaFields) {
        state.value.area.value = event.area
        val newArea =
            state.value.getArea.data?.getChangedFields(state.value.area.value) ?: return
        state.update {
            it.copy(isEditEnabled = !newArea.isEmpty)
        }
    }

    private fun addArea() =
        getData(
            getData = { addEditAreaUseCase.insertArea(state.value.area.value) },
            getState = { state.value.addEditArea },
            setState = { state.value = state.value.copy(addEditArea = it) },
            onSuccess = { p ->
                state.update {
                    it.copy(
                        getArea = State.success(p),
                    )
                }

                state.value.area.value = p

                toastManager.success(
                    description = "Area created successfully",
                )
            },
        )

    private fun editArea() =
        getData(
            getData = { addEditAreaUseCase.updateArea(state.value.area.value) },
            getState = { state.value.addEditArea },
            setState = { state.value = state.value.copy(addEditArea = it) },
            onSuccess = { ta ->
                state.update {
                    it.copy(
                        getArea = State.success(ta),
                    )
                }

                state.value.area.value = ta

                toastManager.success(
                    description = "Area updated successfully",
                )
            },
        )

    private fun deleteArea() =
        getData(
            getData = { areaRepository.delete(state.value.area.value) },
            getState = { state.value.deleteArea },
            setState = { state.value = state.value.copy(deleteArea = it) },
            onSuccess = {
                toastManager.success(
                    description = "Area deleted successfully",
                )
            },
        )

    private fun clearState() {
        state.value = state.value.copy(
            getArea = State(),
            addEditArea = State(),
            deleteArea = State(),
            getAllCountries = State(),
            getProposalsAreaCount = State(),
            getSuppliersAreaCount = State(),
            addCountry = State(),
            isEditEnabled = false,
        )

        state.value.area.value = AreaModel.empty
    }

    private fun addCountry(event: AddEditAreaEvent.AddCountry) =
        getData(
            getData = { countryRepository.insert(CountryModel.empty.copy(name = event.title)) },
            getState = { state.value.addCountry },
            setState = { state.value = state.value.copy(addCountry = it) },
            onSuccess = { c ->
                state.update {
                    it.copy(
                        getAllCountries = State.success(it.getAllCountries.data.orEmpty() + c),
                    )
                }
            },
        )
}